import { Component } from "@angular/core";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { TransactionsService } from "../_services/transactions.service";
import { MessageService } from "primeng/api";
import { firstValueFrom } from "rxjs";
import { IParamsCollection } from "src/app/core/jsonapi/interfaces/params-collection";
import { FilterBuilder } from "src/app/core/jsonapi/filters-builder";
import { Filter, FilterOperation } from "src/app/core/jsonapi/filter";
import { TransactionsTypesService } from "../_services/transactions-types.service";
import { Contract } from "../../companies/_types/contract";
import { ContractTransactionSeriesService } from "../../companies/_services/contracts-transactions-series.service";
import { PaymentsMethodsService } from "../../common/_services/payments-methods.service";
import { PaymentMethod } from "../../common/_types/payment-method";
import { BusinessBankAccountsService } from "../../business/_services/business-bank-accounts.service";
import { BusinessBankAccount } from "../../business/_types/business-bank-account";
import { Transaction } from "../_types/transaction";
import { File } from "../../common/_types/file";
import { Company } from "../../companies/_types/company";
import { ContractsService } from "../../companies/_services/contracts.service";
import { CompaniesService } from "../../companies/_services/companies.service";

import * as XLSX from 'xlsx';

@Component({
    selector: 'app-generate-invoice-csv',
    templateUrl: './generate-invoice-csv.component.html',
    styleUrls: ['./generate-invoice-csv.component.scss'],
    standalone: false
})
export class GenerateInvoiceCsvComponent {

  public transactionSeries: any[] = [];
  public paymentsMethods: PaymentMethod[] = [];
  public billingCompaniesBanksAccounts: BusinessBankAccount[] = [];
  public transactions: Transaction[] = [];
  public company?: Company;
  public companies: Company[] = [];
  public contract?: Contract;
  public contracts: Contract[] = [];
  public selectedPaymentMethodRequiresBank: boolean = false;
  public subtotalTransactions: number = 0;
  public subtotalTaxAmount: number = 0;
  public totalTransactions: number = 0;
  public file?: File;
  public step:string = "prepare"; //preapre,generate,complete
  public parseResults: any[] = [];
  public csvErrors: number = 0;

  public invoice: any = {
    date: new Date(),
    isPayed: false
  };

  constructor(
    private config: DynamicDialogConfig,
    private dialogRef: DynamicDialogRef,
    private transactionsService: TransactionsService,
    private transactionsTypesService: TransactionsTypesService,
    private companiesService: CompaniesService,
    private contractsService: ContractsService,
    private contractTransactionSeriesService: ContractTransactionSeriesService,
    private paymentsMethodsService: PaymentsMethodsService,
    private businessBankAccountsService: BusinessBankAccountsService,
    private messageService: MessageService
  ) {
  }

  async ngOnInit(): Promise<void> {
    //cargamos compañias
    await this.loadCompanies();
    
    //preparamos datos de la factura
    await this.prepareInvoiceData();
  }

  async loadCompanies(){
    let params: IParamsCollection = {
      sort: ['name'],
    };

    let response = await firstValueFrom(this.companiesService.all(params));
    this.companies = response.data;
  }

  async onCompanySelect(companyId: string){
    this.company = this.companies.find(c => c.id === companyId);
    await this.loadContracts(companyId);
  }

  async loadContracts(companyId: string){
    let filter : any = {
      companyId: new Filter(FilterOperation.equals, 'companyId', '', companyId)
    };
    let params: IParamsCollection = {
      filter: FilterBuilder.fromObject(filter).build(),
      sort: ['name'],
      include: ['business']
    };
    let response = await firstValueFrom(this.contractsService.all(params));
    this.contracts = response.data;
  }

  async onContractSelect(contractId: string){
    this.contract = this.contracts.find(c => c.id === contractId);
    await this.loadTransactionSeries();
    await this.loadPaymentsMethods();
    await this.loadBillingCompaniesBankAccounts();
  }

  async loadTransactionSeries() {
    
    //Obtenemos el id del tipo de transacición factura de empresa
    let paramsTransactionType: IParamsCollection = {
      include: []
    }

    //creamos filtros
    paramsTransactionType.filter = FilterBuilder.fromObject({ code: new Filter(FilterOperation.equals, 'code', '', "company_outgoing_invoice") }).build();

    let responseTransactionType = await firstValueFrom(this.transactionsTypesService.all(paramsTransactionType));
    let transactionTypeInvoice = responseTransactionType.data[0];

    let filters: any = {};
    if (this.contract != null) {
      filters.contractId = new Filter(FilterOperation.equals, 'contractId', "", this.contract.id);
    }

    filters.transactionTypeId = new Filter(FilterOperation.equals, 'transactionSerie.transactionTypeId', "", transactionTypeInvoice.id);

    let params: IParamsCollection = {
      page: { size: 0 },
      include: ['transactionSerie'],
      filter: FilterBuilder.fromObject(filters).build()
    };
    let response = await firstValueFrom(this.contractTransactionSeriesService.all(params));
    this.transactionSeries = response.data.map((m: any) => {
      return m.transactionSerie;
    });
    this.transactionSeries.push({id: -1, name: "Sin serie"});

    if(this.transactionSeries.length==1) this.invoice.transactionSerieId = this.transactionSeries[0].id;
    
  }

  async loadPaymentsMethods() {
    let response = await firstValueFrom(this.paymentsMethodsService.all({ page: { number: 1, size: 10 } }));
    this.paymentsMethods = response.data;

    let paymentMethod = this.paymentsMethods.find(m => m.id === this.contract?.paymentMethodId);
    if (paymentMethod){
      this.invoice.paymentMethodId = paymentMethod.id;
      this.onPaymentMethodChange(paymentMethod.id!);
    }
  }

  async loadBillingCompaniesBankAccounts() {

    let filters: any = {};
    if (this.contract?.billingCompanyId != null) {
      filters.companyId = new Filter(FilterOperation.equals, 'businessId', "", this.contract.billingCompanyId);
    }

    let params: IParamsCollection = {
      page: { size: 0 },
      filter: FilterBuilder.fromObject(filters).build()
    };
    let response = await firstValueFrom(this.businessBankAccountsService.all(params));
    this.billingCompaniesBanksAccounts = response.data;
    
    //autoseleccionamos la del contrato si está activa
    let contractBankAccount = this.billingCompaniesBanksAccounts.find(m=>m.id == this.contract?.billingCompanyBankAccountId);
    if(contractBankAccount) this.invoice.billingCompanyBankAccountId = contractBankAccount.id;
  }

  async prepareInvoiceData(){
    this.invoice = {};
    //Obtenemos los datos del expediente para tener el contrato            
    this.invoice.paymentDuedateDays = this.contract?.paymentDuedateDays;
    this.invoice.paymentMethodId = this.contract?.paymentMethodId;
    this.onPaymentMethodChange(this.invoice.paymentMethodId);
    this.invoice.billingCompanyBankAccountId = this.contract?.billingCompanyBankAccountId;
    this.invoice.date = new Date();
    //Añadimos a fecha los dias de vencimiento de contrato
    this.invoice.paymentDuedate = new Date(this.invoice.date);
    this.invoice.paymentDuedate.setDate(this.invoice.paymentDuedate.getDate() + (this.contract?.paymentDuedateDays ?? 0));       
    if(this.transactionSeries.length>0) this.invoice.transactionSerieId = this.transactionSeries[0].id; 
  }

  onPaymentMethodChange(paymentMethodId: string): void {
    const selectedMethod = this.paymentsMethods.find(m => m.id === paymentMethodId);
    this.selectedPaymentMethodRequiresBank = selectedMethod?.businessBankAccountEnabled === true;
  }

  onInvoiceDateChange(date: Date): void {
    const daysToAdd = this.invoice.paymentDuedateDays; // O cualquier valor que necesites
    if (date) {
      const dueDate = new Date(date);
      if(daysToAdd) dueDate.setDate(dueDate.getDate() + daysToAdd);
      this.invoice.paymentDuedate = dueDate;
    }
  }

  async onFileSelect(event: any) {
    this.file = event.files[0];
  }

  async parseCSV(){
    if(!this.file){
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Debe seleccionar un archivo CSV.'
      });
      return;
    }

    if(this.company == null || this.contract == null){
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Debe seleccionar una compañía y un contrato válidos.'
      });
      return;
    }

    //enviamos al servidor el csv para revisarlo
    this.parseResults = [];
    let result = await firstValueFrom(this.transactionsService.deliveryNotesCsvCheck(this.file, this.company!.id!, this.contract!.id!));
    if(result.success === false){
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: result.message
      });
      return;
    }else{
      this.parseResults = result.data;

      //calculamos errores
      this.csvErrors = this.parseResults.filter(r => !r.importable).length;

      //calculamos el importe total que podrá generarse
      this.subtotalTransactions = 0;
      this.subtotalTaxAmount = 0;
      this.totalTransactions = 0;
      this.parseResults.forEach(r => {
        if(r.importable){
          this.subtotalTransactions += r.invoiceSubtotal;
          this.subtotalTaxAmount += r.invoiceSubtotal * 0.21;
          this.totalTransactions += r.invoiceSubtotal * 1.21;
        }
      });

      this.step = "generate";
    }
  }

  async generateInvoice(){        

    if ((!this.invoice.transactionSerieId && (this.invoice.code == null || this.invoice.code.trim() ==""))||(this.invoice.transactionSerieId == -1 && (this.invoice.code == null || this.invoice.code.trim() ==""))) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Debe seleccionar una serie de factura o añadir un código manual.'
      });
      return;
    }

    if (!this.company || !this.contract) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Debe seleccionar una compañía y un contrato válidos.'
      });
      return;
    }

    //enviamos al servidor el csv para facturarlo
    let transactionSerieId = this.invoice.transactionSerieId;
    if(transactionSerieId == -1) transactionSerieId = null;
    let result = await firstValueFrom(this.transactionsService.deliveryNotesCsvProcess(this.file, this.company.id!, this.contract.id!, this.invoice.code, this.invoice.date, this.invoice.paymentMethodId, this.invoice.paymentDuedate, this.invoice.billingCompanyBankAccountId, transactionSerieId));
    if(result!=null){
      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Factura generada correctamente.'
      });
      this.dialogRef.close();
    }else{
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Se ha producido un error al generar la factura.'
      });
    }

  }

  async close(){
    this.dialogRef.close();
  }
  
  async exportXLSX(){
    if(this.parseResults.length==0) return;

    let headers = [[
      'Nº albaran',
      'Fecha factura',
      'Nº factura',
      'Neto',
      'Total',
      'Estado pago',
      'Fecha pago',
      'Importable',
      'Diferencia',
      'Información'
    ]];
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet([]);
    XLSX.utils.sheet_add_aoa(ws, headers);
    let data = this.parseResults.map(r => {
      return {
        deliveryNoteNumber: r.caseNotificationNumberCode,
        invoiceDate: r.invoiceDate,
        invoiceNumber: r.invoiceNumber,
        invoiceSubtotal: r.invoiceSubtotal,
        invoiceTotal: r.invoiceTotal,
        isPayed: r.isPayed ? 'Sí' : 'No',
        paymentDate: r.paymentDate,
        importable: r.importable ? 'Sí' : 'No',
        differenceAmount: r.diff,
        information: r.info
      };
    });
    XLSX.utils.sheet_add_json(ws, data, { origin: 'A2', skipHeader: true });
    XLSX.utils.book_append_sheet(wb, ws, 'Resultados');
    XLSX.writeFile(wb, 'multi-invoice-results.xlsx');
  }

}
